<app-page *ngIf="pageStatus$ | async as pageStatus">
  <app-page-header pageTitle="Collections">
    <!-- Save controls -->
    <div *ngIf="canEditSettings$ | async" class="d-flex flex-row align-items-center">
      <mat-hint>Note: Changes will only apply to new statements.</mat-hint>

      <button type="button" class="mx-4" (click)="onRevert()" andButton color="default">Revert</button>

      <button
        [disabled]="!(canSave$ | async)"
        [isLoading]="isUpdateProcessing$ | async"
        (click)="updateSettings()"
        andButton
        color="primary"
      >
        Save Settings
      </button>
    </div>
  </app-page-header>

  <app-page-content>
    <div class="mx-0 settings-content flex-1 overflow-auto d-flex flex-column align-items-center">
      <!-- Form content -->
      <ng-container [ngSwitch]="pageStatus">
        <!-- Loading the settings to display -->
        <div *ngSwitchCase="LoadableStatus.LOADING" class="h-100 d-flex flex-column align-items-center justify-content-center">
          <mat-spinner />
        </div>

        <!-- Settings failed to load -->
        <div *ngSwitchCase="LoadableStatus.ERROR" class="h-100 d-flex flex-column align-items-center justify-content-center">
          <div class="alert alert--error ms-4 me-4">
            <p>Failed to load Collections settings</p>
            <p>Please try your request again later.</p>
          </div>
        </div>

        <!-- Settings container -->
        <ng-container *ngSwitchCase="LoadableStatus.LOADED">
          <h3 class="h3-500">
            Configure when statements will be sent to {{ statementRecipientLabel$ | async }}s, and how the emails will appear.
          </h3>

          <div *ngIf="didUpdateFail$ | async" #failedSaveAlert class="alert alert--error">
            <p>Failed to process save request</p>
            <p>Please try again later.</p>
          </div>

          <!-- Throttling Form -->
          <app-collections-throttling
            *ngIf="throttlingSettings$ | async as throttlingSettings"
            class="mt-6"
            [throttlingState]="throttlingSettings"
            [disabled]="(canEditSettings$ | async) === false"
            (isValid)="isThrottlingFormValid$.next($event)"
            (throttlingStateChange)="onThrottleChange($event)"
          />

          <!-- Triggers Form -->
          <sequence-trigger-form
            *ngIf="triggersSettings$ | async as triggersSettings"
            class="d-block mt-6"
            [noTriggersStatementEnabled]="triggersSettings.noTriggerStatementEnabled"
            [noTriggerStatementDay]="triggersSettings.noTriggerStatementDay"
            [noTriggerStatementOccurrence]="triggersSettings.noTriggerStatementOccurrence"
            [triggers]="triggersSettings.expressionList ?? triggersSettings.triggers"
            [disabled]="(canEditSettings$ | async) === false"
            (isValid)="isTriggersFormValid$.next($event)"
            (sequenceTriggerUpdate)="onTriggerChange($event)"
            (triggerUpdateV2)="onTriggerChangeV2($event)"
          />

          <app-excluded-clients class="mt-6" [disabled]="(canEditSettings$ | async) === false" />

          <!-- Auto-approve Form -->
          <app-collections-auto-approve
            *ngIf="{ value: autoApproveSetting$ | async } as autoApproveSetting"
            class="mt-6"
            [autoApprove]="autoApproveSetting.value"
            [disabled]="(canEditSettings$ | async) === false"
            (autoApproveChange)="onAutoApproveChange($event)"
          />

          <app-collections-bucket-message-form
            class="mt-6"
            [buckets]="bucketSettings$ | async"
            [messageVariables]="dynamicFields$ | async"
            [disabled]="(canEditSettings$ | async) === false"
            (bucketsChange)="onBucketChange($event)"
            (isValid)="isBucketMessagesFormValid$.next($event)"
          />

          <!-- Additional recipients -->
          <app-additional-recipients-input
            class="mt-6"
            [additionalRecipients]="additionalRecipients$ | async"
            [clientPartnerEmailNotificationType]="clientPartnerNotificationType$ | async"
            [clientPartnerSelectedBuckets]="clientPartnerBuckets$ | async"
            [disabled]="(canEditSettings$ | async) === false"
            (additionalRecipientsChange)="onAdditionalRecipientsChange($event)"
            (clientPartnerEmailNotificationTypeChange)="onClientPartnerNotificationTypeChange($event)"
            (clientPartnerSelectedBucketsChange)="onClientPartnerBucketsChange($event)"
            (isValid)="isBucketMessagesFormValid$.next($event)"
          />

          <!-- Sender information -->
          <app-collections-addresses
            *ngIf="messageSettings$ | async as messageSettings"
            class="d-block mt-6"
            [fromName]="messageSettings.fromName"
            [includeCcAndBcc]="false"
            [replyToAddress]="messageSettings.from"
            [disabled]="(canEditSettings$ | async) === false"
            (fromNameChange)="onFromNameChange($event)"
            (isValid)="isAddressFormValid$.next($event)"
            (replyToAddressChange)="onReplyToChange($event)"
          />
        </ng-container>
      </ng-container>
    </div>
  </app-page-content>
</app-page>
